import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Target, CheckCircle2, Circle, ArrowLeft, TrendingUp, Rocket, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { DictationButton } from '../components/DictationButton';

// Domain labels for mission display
const DOMAIN_LABELS = {
  en: {
    physical_health: 'Physical Health',
    strength_fitness: 'Strength / Fitness',
    energy: 'Energy',
    environment_order: 'Space / Order',
    work_mission: 'Work / Mission',
    money_financial: 'Money / Financial',
    love_relationship: 'Love / Relationship',
    friends_community: 'Friends / Community',
    emotional_stability: 'Emotional Stability',
    discipline_consistency: 'Discipline',
    learning_growth: 'Learning / Growth',
    meaning_purpose: 'Meaning / Purpose',
  },
  tr: {
    physical_health: 'Fiziksel Sağlık',
    strength_fitness: 'Güç / Fitness',
    energy: 'Enerji',
    environment_order: 'Alan / Düzen',
    work_mission: 'İş / Misyon',
    money_financial: 'Para / Finansal',
    love_relationship: 'Aşk / İlişki',
    friends_community: 'Arkadaşlar / Topluluk',
    emotional_stability: 'Duygusal Denge',
    discipline_consistency: 'Disiplin',
    learning_growth: 'Öğrenme / Gelişim',
    meaning_purpose: 'Anlam / Amaç',
  }
};

export const WeeklyGoalsCheckIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [habits, setHabits] = useState([]);
  
  // Get mission from navigation state
  const activeMission = location.state?.mission || null;
  const [missionNotes, setMissionNotes] = useState('');
  const [missionStatus, setMissionStatus] = useState(activeMission?.status || 'active');
  const [togglingMission, setTogglingMission] = useState(false);

  const handleToggleMission = async () => {
    if (!activeMission) return;
    setTogglingMission(true);
    try {
      const endpoint = missionStatus === 'completed' 
        ? `/missions/${activeMission.id}/uncomplete`
        : `/missions/${activeMission.id}/complete`;
      await api.patch(endpoint);
      const newStatus = missionStatus === 'completed' ? 'active' : 'completed';
      setMissionStatus(newStatus);
      toast.success(newStatus === 'completed' 
        ? (language === 'tr' ? 'Misyon tamamlandı!' : 'Mission completed!')
        : (language === 'tr' ? 'Misyon tekrar aktif' : 'Mission reactivated'));
    } catch (error) {
      toast.error(language === 'tr' ? 'Güncelleme başarısız' : 'Update failed');
    } finally {
      setTogglingMission(false);
    }
  };

  useEffect(() => {
    fetchCurrentGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentGoals = async () => {
    try {
      const response = await api.get('/assessment/current');
      const aiAnalysis = response.data.analysis;
      
      setAnalysis(aiAnalysis);
      setPriorities(
        aiAnalysis.priorities.map((p, i) => ({
          index: i,
          text: p,
          score: 50,
          notes: ''
        }))
      );
      setHabits(
        aiAnalysis.habits.map((h, i) => ({
          index: i,
          text: h,
          completed: false
        }))
      );
    } catch (error) {
      toast.error(t('goalsCheckin.loadFailed'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post('/goals/weekly-checkin', {
        priorities_progress: priorities,
        habits_completed: habits
      });
      toast.success(t('goalsCheckin.success'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(t('goalsCheckin.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#D4AF37]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative py-16 px-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-white/70 hover:text-white transition-colors duration-300 uppercase tracking-widest text-sm font-sans mb-8 flex items-center gap-2"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('nav.back')}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Target className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="font-serif font-light text-4xl text-[#F8F5F2] mb-4">
            {t('goalsCheckin.title')}
          </h1>
          <p className="font-sans font-light text-[#D1D1D6] text-lg">
            {t('goalsCheckin.subtitle')}
          </p>
        </motion.div>

        {/* Active Mission Card - Highlighted */}
        {activeMission && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.05 }}
            className={`glass-panel rounded-2xl p-6 mb-8 border-2 ${
              missionStatus === 'completed' 
                ? 'border-[#D4AF37] bg-[#D4AF37]/15' 
                : 'border-[#D4AF37]/50 bg-[#D4AF37]/10'
            }`}
          >
            <div className="flex items-start gap-4">
              <button
                onClick={handleToggleMission}
                disabled={togglingMission}
                className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 hover:bg-[#D4AF37]/30 transition-colors"
                data-testid="toggle-mission-complete"
              >
                {togglingMission ? (
                  <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
                ) : missionStatus === 'completed' ? (
                  <CheckCircle2 className="w-6 h-6 text-[#D4AF37]" />
                ) : (
                  <Circle className="w-6 h-6 text-[#D4AF37]" />
                )}
              </button>
              <div className="flex-1">
                <p className="font-sans uppercase tracking-[0.15em] text-[#D4AF37] text-xs font-semibold mb-2">
                  {language === 'tr' ? 'Bu Haftanın Misyonu' : 'This Week\'s Mission'}
                </p>
                <h3 className={`font-serif text-xl mb-2 ${
                  missionStatus === 'completed' ? 'text-[#A1A1AA] line-through' : 'text-[#F8F5F2]'
                }`}>
                  {activeMission.text}
                </h3>
                <p className="text-sm text-[#A1A1AA] mb-4">
                  {(DOMAIN_LABELS[language] || DOMAIN_LABELS.en)[activeMission.domain]}
                  {missionStatus === 'completed' && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[#D4AF37]">
                      <CheckCircle2 className="w-4 h-4" />
                      {language === 'tr' ? 'Tamamlandı' : 'Completed'}
                    </span>
                  )}
                </p>
                
                {/* Notes Field */}
                <div>
                  <label className="text-xs text-[#6B7280] uppercase tracking-wider mb-2 block">
                    {language === 'tr' ? 'Notlar & Yansımalar' : 'Notes & Reflections'}
                  </label>
                  <div className="relative">
                    <textarea
                      value={missionNotes}
                      onChange={(e) => setMissionNotes(e.target.value)}
                      placeholder={language === 'tr' 
                        ? 'Bu misyonla ilgili ilerlemenizi, engellerinizi veya düşüncelerinizi yazın...' 
                        : 'Write about your progress, blockers, or thoughts on this mission...'}
                      className="w-full px-4 py-3 pr-12 rounded-xl bg-black/40 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none text-sm resize-none"
                      rows={3}
                      data-testid="mission-notes"
                    />
                    <div className="absolute right-2 top-2">
                      <DictationButton 
                        onTranscript={(text) => setMissionNotes(prev => prev ? `${prev} ${text}` : text)}
                        className="!p-1.5"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Priorities Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-panel rounded-2xl p-8 mb-8"
        >
          <h2 className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-6">
            {t('goalsCheckin.prioritiesTitle')}
          </h2>
          <div className="space-y-6">
            {priorities.map((priority, index) => (
              <div key={index} className="space-y-3" data-testid={`priority-${index}`}>
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => {
                      const newPriorities = [...priorities];
                      newPriorities[index].score = newPriorities[index].score >= 100 ? 50 : 100;
                      setPriorities(newPriorities);
                    }}
                    className="mt-0.5 flex-shrink-0"
                    data-testid={`priority-complete-${index}`}
                  >
                    {priority.score >= 100 ? (
                      <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                    ) : (
                      <Circle className="w-5 h-5 text-[#6B7280] hover:text-[#D4AF37]" />
                    )}
                  </button>
                  <p className={`font-sans flex-1 ${priority.score >= 100 ? 'text-[#A1A1AA] line-through' : 'text-[#F8F5F2]'}`}>{priority.text}</p>
                </div>
                <div className="flex items-center gap-4 pl-8">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={priority.score}
                    onChange={(e) => {
                      const newPriorities = [...priorities];
                      newPriorities[index].score = parseInt(e.target.value);
                      setPriorities(newPriorities);
                    }}
                    className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#D4AF37]"
                    data-testid={`priority-slider-${index}`}
                  />
                  <span className="text-[#D4AF37] font-sans font-semibold text-xl w-16 text-right">
                    {priority.score}
                  </span>
                </div>
                <div className="relative">
                  <textarea
                    value={priority.notes}
                    onChange={(e) => {
                      const newPriorities = [...priorities];
                      newPriorities[index].notes = e.target.value;
                      setPriorities(newPriorities);
                    }}
                    placeholder={t('goalsCheckin.notesPlaceholder')}
                    className="w-full bg-white/5 border border-[#D4AF37]/15 rounded-lg px-4 py-2 pr-12 text-[#F8F5F2] font-sans text-sm focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none"
                    rows="2"
                    data-testid={`priority-notes-${index}`}
                  />
                  <div className="absolute right-2 top-2">
                    <DictationButton 
                      onTranscript={(text) => {
                        const newPriorities = [...priorities];
                        newPriorities[index].notes = newPriorities[index].notes 
                          ? `${newPriorities[index].notes} ${text}` 
                          : text;
                        setPriorities(newPriorities);
                      }}
                      className="!p-1.5"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Habits Completion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-panel rounded-2xl p-8 mb-8"
        >
          <h2 className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-6">
            {t('goalsCheckin.habitsTitle')}
          </h2>
          <div className="space-y-4">
            {habits.map((habit, index) => (
              <button
                key={index}
                onClick={() => {
                  const newHabits = [...habits];
                  newHabits[index].completed = !newHabits[index].completed;
                  setHabits(newHabits);
                }}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all hover:border-[#D4AF37]/40 text-left group"
                style={{
                  borderColor: habit.completed
                    ? 'rgba(212, 175, 55, 0.6)'
                    : 'rgba(212, 175, 55, 0.15)',
                  backgroundColor: habit.completed
                    ? 'rgba(212, 175, 55, 0.1)'
                    : 'transparent',
                }}
                data-testid={`habit-${index}`}
              >
                {habit.completed ? (
                  <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
                ) : (
                  <Circle className="w-6 h-6 text-[#D4AF37]/30 flex-shrink-0" />
                )}
                <span className="font-sans text-[#F8F5F2] flex-1">{habit.text}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-12 py-4 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.15)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] font-sans text-lg disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="submit-button"
        >
          {submitting ? t('goalsCheckin.saving') : (
            <>
              <TrendingUp className="w-5 h-5" />
              {t('goalsCheckin.saveProgress')}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default WeeklyGoalsCheckIn;
